import type {
  CreateDeploymentRequestBody,
  DeleteDeploymentRequestBody,
  DeploymentDetailsResponse,
  DeploymentResponse,
  GetDeploymentDetailsRequestBody,
  GetNeo4jConfigsRequestBody,
  Neo4jConfigResponse,
  RenameDeploymentRequestBody,
  UpdateConnectionUrlRequestBody,
} from '../api-types';
import {
  transformGetDeploymentConfigsResponse,
  transformGetDeploymentDetailsResponse,
  transformGetDeploymentsResponse,
} from '../transformers/topology';
import type * as DeploymentTypes from '../types/topology';
import type { Builder } from './types';

const BASE_PATH = '/v1/user';

export const topologyEndpoints = (builder: Builder) => ({
  createDeployment: builder.mutation<string, CreateDeploymentRequestBody>({
    query: (req) => ({
      url: `${BASE_PATH}/deployments`,
      method: 'POST',
      params: { projectId: req.project_id },
      headers: { 'Project-Id': req.project_id },
      body: req,
    }),
    invalidatesTags: (result, error, { project_id }) => [{ type: 'Topology', id: project_id }],
    transformResponse: (res: DeploymentResponse): string => res.deployment_id,
  }),
  getDeployments: builder.query<DeploymentTypes.Deployment[], string>({
    query: (projectId) => ({
      url: `${BASE_PATH}/deployments`,
      params: { projectId: projectId },
      headers: { 'Project-Id': projectId },
    }),
    providesTags: (result, error, projectId) => [{ type: 'Topology', id: projectId }],
    transformResponse: (res: DeploymentResponse[]): DeploymentTypes.Deployment[] =>
      transformGetDeploymentsResponse(res),
  }),
  getDeploymentDetails: builder.query<DeploymentTypes.DeploymentDetails, GetDeploymentDetailsRequestBody>({
    query: (getDbmsDetailsRequest) => ({
      url: `${BASE_PATH}/deployments/${getDbmsDetailsRequest.deploymentId}`,
      params: { projectId: getDbmsDetailsRequest.projectId },
      headers: { 'Project-Id': getDbmsDetailsRequest.projectId },
    }),
    providesTags: (result, error, getDbmsDetailsRequest) => [
      { type: 'Topology', id: getDbmsDetailsRequest.deploymentId },
    ],
    transformResponse: (res: DeploymentDetailsResponse): DeploymentTypes.DeploymentDetails =>
      transformGetDeploymentDetailsResponse(res),
  }),
  getNeo4jConfigs: builder.query<DeploymentTypes.Neo4jConfig[], GetNeo4jConfigsRequestBody>({
    query: (getDeploymentConfigRequest) => ({
      url: `${BASE_PATH}/deployments/${getDeploymentConfigRequest.deploymentId}/neo4j-configs`,
      params: { projectId: getDeploymentConfigRequest.projectId },
      headers: { 'Project-Id': getDeploymentConfigRequest.projectId },
    }),
    providesTags: (result, error, getDeploymentConfigRequest) => [
      { type: 'Topology', id: getDeploymentConfigRequest.deploymentId },
    ],
    transformResponse: (res: Neo4jConfigResponse[]): DeploymentTypes.Neo4jConfig[] =>
      transformGetDeploymentConfigsResponse(res),
  }),
  renameDeployment: builder.mutation<boolean, RenameDeploymentRequestBody>({
    query: (req) => ({
      url: `${BASE_PATH}/deployments/${req.deploymentId}`,
      method: 'PATCH',
      params: { projectId: req.projectId },
      headers: { 'Project-Id': req.projectId },
      body: { name: req.name },
    }),
    invalidatesTags: (result, error, req) => [
      { type: 'Topology', id: req.projectId },
      { type: 'Topology', id: req.deploymentId },
    ],
  }),
  deleteDeployment: builder.mutation<boolean, DeleteDeploymentRequestBody>({
    query: (req) => ({
      url: `${BASE_PATH}/deployments/${req.deploymentId}`,
      method: 'DELETE',
      params: { projectId: req.projectId },
      headers: { 'Project-Id': req.projectId },
    }),
    invalidatesTags: (result, error, req) => [
      { type: 'Topology', id: req.projectId },
      { type: 'Topology', id: req.deploymentId },
      { type: 'Tokens', id: req.deploymentId },
    ],
  }),
  updateConnectionUrl: builder.mutation<boolean, UpdateConnectionUrlRequestBody>({
    query: (req) => ({
      url: `${BASE_PATH}/deployments/${req.deploymentId}/connectionUrl`,
      method: 'PATCH',
      params: { projectId: req.projectId },
      headers: { 'Project-Id': req.projectId },
      body: { connection_url: req.connectionUrl },
    }),
    invalidatesTags: (result, error, req) => [
      { type: 'Topology', id: req.projectId },
      { type: 'Topology', id: req.deploymentId },
    ],
  }),
});
